import { SyntheticEvent, useRef, useState } from 'react'

import { sliderPositionToVolume } from './VideoPlayer.utils'
import { UseVideoVolumeOptions } from './useVideoVolume.types'

export const useVideoVolume = ({
  videoRef,
  onVolumeChange
}: UseVideoVolumeOptions) => {
  const lastAudibleVolumeRef = useRef(1)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)

  const syncVolume = (video: HTMLVideoElement) => {
    setVolume(video.volume)
    setIsMuted(video.muted)
  }

  const handleVolumeChange = (event: SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget
    syncVolume(video)
    if (!video.muted && video.volume > 0) {
      lastAudibleVolumeRef.current = video.volume
    }
    onVolumeChange?.(event)
  }

  const setVolumeFromSlider = (sliderPosition: number) => {
    const video = videoRef.current
    if (!video) return

    const value = sliderPositionToVolume(sliderPosition)
    video.volume = value
    video.muted = false
    setVolume(value)
    setIsMuted(false)
    if (value > 0) lastAudibleVolumeRef.current = value
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return

    if (video.muted || video.volume === 0) {
      const restoredVolume = lastAudibleVolumeRef.current || 1
      video.volume = restoredVolume
      video.muted = false
      setVolume(restoredVolume)
      setIsMuted(false)
    } else {
      lastAudibleVolumeRef.current = video.volume
      video.muted = true
      setIsMuted(true)
    }
  }

  return {
    handleVolumeChange,
    isMuted,
    setVolumeFromSlider,
    syncVolume,
    toggleMute,
    volume
  }
}
